import { z } from 'zod';

/** Platform vertical slugs — keys of `PLATFORM_VERTICAL_MATRIX`. */
export const PLATFORM_VERTICAL_KEYS = {
  restaurant: 'restaurant',
  cafe: 'cafe',
  bar: 'bar',
  foodTruck: 'food_truck',
} as const;

export const PLATFORM_VERTICAL_KEY_VALUES = [
  PLATFORM_VERTICAL_KEYS.restaurant,
  PLATFORM_VERTICAL_KEYS.cafe,
  PLATFORM_VERTICAL_KEYS.bar,
  PLATFORM_VERTICAL_KEYS.foodTruck,
] as const;

export type PlatformVerticalKey = (typeof PLATFORM_VERTICAL_KEY_VALUES)[number];
export const PlatformVerticalKeySchema = z.enum(PLATFORM_VERTICAL_KEY_VALUES);

/** Package tier slugs — keys of each vertical's `tiers` map. */
export const PACKAGE_TIER_KEYS = {
  minimum: 'minimum',
  recommended: 'recommended',
  plus: 'plus',
} as const;

export const PACKAGE_TIER_KEY_VALUES = [
  PACKAGE_TIER_KEYS.minimum,
  PACKAGE_TIER_KEYS.recommended,
  PACKAGE_TIER_KEYS.plus,
] as const;

export type PackageTierKey = (typeof PACKAGE_TIER_KEY_VALUES)[number];
export const PackageTierKeySchema = z.enum(PACKAGE_TIER_KEY_VALUES);

/** Named theme slugs — use as `THEME_REGISTRY` keys and project `themeId`. */
export const THEME_ID = {
  michelinDark: 'michelin-dark',
  bistroBrasserie: 'bistro-brasserie',
  hinokiCounter: 'hinoki-counter',
  /** Vietnamese neighborhood — herb steam / broth warmth. */
  lotusSteam: 'lotus-steam',
  /** Vietnamese neighborhood — tiled shopfront / awning market (not a Lotus Steam retint). */
  tileAwning: 'tile-awning',
  /** Pan-Asian neighborhood court — dusk wood / lantern (not sushi). */
  lanternCourt: 'lantern-court',
  /** Fine dining — daylight limestone / glasshouse tasting (not Michelin anthracite). */
  limestoneOrangery: 'limestone-orangery',
  /** Fine dining — north-German estate hearth (not bistro Gasthaus). */
  moorlandHearth: 'moorland-hearth',
  brutalistGrind: 'brutalist-grind',
  earthClay: 'earth-clay',
  neonNoir: 'neon-noir',
  velvetVintage: 'velvet-vintage',
  /** Imbiss — Döner / grill window (Street Board + call). */
  boldStreet: 'bold-street',
  /** Imbiss — Currywurst / German street rail (not Döner). */
  mustardRail: 'mustard-rail',
  /** Imbiss — Falafel / mezze window (herb stall, not grill neon). */
  tahiniWindow: 'tahini-window',
  cleanNomad: 'clean-nomad',
} as const;

export const THEME_ID_KEYS = [
  THEME_ID.michelinDark,
  THEME_ID.bistroBrasserie,
  THEME_ID.hinokiCounter,
  THEME_ID.lotusSteam,
  THEME_ID.tileAwning,
  THEME_ID.lanternCourt,
  THEME_ID.limestoneOrangery,
  THEME_ID.moorlandHearth,
  THEME_ID.brutalistGrind,
  THEME_ID.earthClay,
  THEME_ID.neonNoir,
  THEME_ID.velvetVintage,
  THEME_ID.boldStreet,
  THEME_ID.mustardRail,
  THEME_ID.tahiniWindow,
  THEME_ID.cleanNomad,
] as const;

export type ThemeId = (typeof THEME_ID_KEYS)[number];
export const ThemeIdSchema = z.enum(THEME_ID_KEYS);

/** Named color-set slugs — scoped per theme in `THEME_REGISTRY`. */
export const COLORSET_ID = {
  champagneNight: 'champagne_night',
  velvetSmoke: 'velvet_smoke',
  platinumDawn: 'platinum_dawn',
  /** Fine-dining day — warm ivory paper + champagne gold (not bistro cream). */
  ivoryChampagne: 'ivory_champagne',
  /** Fine-dining day — cool porcelain + soft platinum ink. */
  porcelainSilver: 'porcelain_silver',
  /** Orangery default — pale limestone paper + leaf-green ink. */
  conservatoryChalk: 'conservatory_chalk',
  /** Orangery — citrus espalier on warm glass. */
  espalierCitrus: 'espalier_citrus',
  /** Orangery evening — cool dusk through the panes. */
  duskPane: 'dusk_pane',
  /** Orangery — soft apricot trellis day paper. */
  apricotTrellis: 'apricot_trellis',
  /** Orangery — frost on the glass, silver leaf. */
  frostPane: 'frost_pane',
  /** Moorland default — peat linen paper + rust hearth. */
  peatLinen: 'peat_linen',
  /** Moorland night — oak smoke walls. */
  oakSmoke: 'oak_smoke',
  /** Moorland — grouse rust on linen. */
  grouseRust: 'grouse_rust',
  /** Moorland — canal mist, cooler estate. */
  canalMist: 'canal_mist',
  /** Moorland — beeswax gold on peat. */
  beeswaxGold: 'beeswax_gold',
  classicBrass: 'classic_brass',
  linenMorning: 'linen_morning',
  copperGlow: 'copper_glow',
  /** Bistro — cool zinc / pewter tabletop (not warm brass). */
  zincApron: 'zinc_apron',
  /** Bistro — evening olive banquet wall (night pick). */
  oliveBanquette: 'olive_banquette',
  espressoCore: 'espresso_core',
  paperWhite: 'paper_white',
  signalOrange: 'signal_orange',
  /** Cafe brutalist — dusty concrete rose. */
  concreteRose: 'concrete_rose',
  /** Cafe brutalist — after-hours matcha on dark grid. */
  matchaGrid: 'matcha_grid',
  terracottaDusk: 'terracotta_dusk',
  sageMist: 'sage_mist',
  /** Earth clay — closed kiln / terracotta fire on soot. */
  kilnEmber: 'kiln_ember',
  /** Earth clay — indigo loom textile. */
  indigoLoom: 'indigo_loom',
  /** Earth clay — pale butter clay slip. */
  butterSlip: 'butter_slip',
  electricViolet: 'electric_violet',
  cyanPulse: 'cyan_pulse',
  midnightGlass: 'midnight_glass',
  /** Neon — acid chartreuse on day-flyer paper. */
  acidChartreuse: 'acid_chartreuse',
  /** Neon — blood-orange filament. */
  bloodOrange: 'blood_orange',
  burgundyLounge: 'burgundy_lounge',
  artDecoGold: 'art_deco_gold',
  smokeVelvet: 'smoke_velvet',
  /** Velvet — deep emerald booth. */
  emeraldBooth: 'emerald_booth',
  /** Velvet — day parlor champagne paper + burgundy ink. */
  champagneAsh: 'champagne_ash',
  streetNeon: 'street_neon',
  asphaltBold: 'asphalt_bold',
  sunsetPop: 'sunset_pop',
  /** Imbiss Bold Street — ketchup / mustard stall (day pick). */
  ketchupMustard: 'ketchup_mustard',
  /** Imbiss Bold Street — soft pide cream, sumac spark. */
  pideCream: 'pide_cream',
  /** Mustard Rail — day paper + mustard stripe. */
  mustardPaper: 'mustard_paper',
  /** Mustard Rail — night asphalt under the Hochbahn. */
  tramAsphalt: 'tram_asphalt',
  /** Mustard Rail — ketchup signal on cream. */
  ketchupSignal: 'ketchup_signal',
  /** Mustard Rail — pommes gold warmth. */
  pommesGold: 'pommes_gold',
  /** Mustard Rail — charcoal grill night. */
  grillChar: 'grill_char',
  /** Tahini Window — hummus paper + herb green. */
  hummusPaper: 'hummus_paper',
  /** Tahini Window — oregano night stall. */
  oreganoNight: 'oregano_night',
  /** Tahini Window — lemon stall daylight. */
  lemonStall: 'lemon_stall',
  /** Tahini Window — sesame cream. */
  sesameCream: 'sesame_cream',
  /** Tahini Window — za'atar dusk. */
  zaatarDusk: 'zaatar_dusk',
  coastalBlue: 'coastal_blue',
  sandDune: 'sand_dune',
  /** Nomad — night pine trail. */
  forestTrail: 'forest_trail',
  /** Nomad — canyon dust road. */
  canyonDust: 'canyon_dust',
  /** Nomad — arctic fog grey-blue. */
  arcticFog: 'arctic_fog',
  slateCounter: 'slate_counter',
  /** Hinoki default — cool nori night so nigiri pops. */
  midnightNori: 'midnight_nori',
  wasabiPaper: 'wasabi_paper',
  /** Sushi — warm rice/shari ivory day paper. */
  shariIvory: 'shari_ivory',
  /** Sushi — warm cedar plank. */
  cedarPlank: 'cedar_plank',
  /** Vietnam lotus-steam — herb mist / light broth paper. */
  herbMist: 'herb_mist',
  clayBroth: 'clay_broth',
  midnightLotus: 'midnight_lotus',
  /** Vietnam — banana-leaf steam green. */
  bananaSteam: 'banana_steam',
  /** Vietnam — saffron / turmeric bowl warmth. */
  saffronBowl: 'saffron_bowl',
  /** Vietnam tile-awning — cool ceramic shopfront. */
  ceramicDawn: 'ceramic_dawn',
  cayenneStripe: 'cayenne_stripe',
  inkShop: 'ink_shop',
  groutLime: 'grout_lime',
  monsoonBlue: 'monsoon_blue',
  /** Asian lantern-court — soft lacquer dusk (id kept; display is Lacquer Dusk). */
  lacquerPaper: 'lacquer_paper',
  inkWash: 'ink_wash',
  /** Asian lantern-court default / poster — soft ember dusk wood + lantern gold. */
  emberLantern: 'ember_lantern',
  /** Asian lantern-court — soft celadon noon paper. */
  celadonCourt: 'celadon_court',
  /** Asian lantern-court — soft plum dusk silk. */
  plumSilk: 'plum_silk',
} as const;

export const COLORSET_ID_KEYS = [
  COLORSET_ID.champagneNight,
  COLORSET_ID.velvetSmoke,
  COLORSET_ID.platinumDawn,
  COLORSET_ID.ivoryChampagne,
  COLORSET_ID.porcelainSilver,
  COLORSET_ID.conservatoryChalk,
  COLORSET_ID.espalierCitrus,
  COLORSET_ID.duskPane,
  COLORSET_ID.apricotTrellis,
  COLORSET_ID.frostPane,
  COLORSET_ID.peatLinen,
  COLORSET_ID.oakSmoke,
  COLORSET_ID.grouseRust,
  COLORSET_ID.canalMist,
  COLORSET_ID.beeswaxGold,
  COLORSET_ID.classicBrass,
  COLORSET_ID.linenMorning,
  COLORSET_ID.copperGlow,
  COLORSET_ID.zincApron,
  COLORSET_ID.oliveBanquette,
  COLORSET_ID.espressoCore,
  COLORSET_ID.paperWhite,
  COLORSET_ID.signalOrange,
  COLORSET_ID.concreteRose,
  COLORSET_ID.matchaGrid,
  COLORSET_ID.terracottaDusk,
  COLORSET_ID.sageMist,
  COLORSET_ID.kilnEmber,
  COLORSET_ID.indigoLoom,
  COLORSET_ID.butterSlip,
  COLORSET_ID.electricViolet,
  COLORSET_ID.cyanPulse,
  COLORSET_ID.midnightGlass,
  COLORSET_ID.acidChartreuse,
  COLORSET_ID.bloodOrange,
  COLORSET_ID.burgundyLounge,
  COLORSET_ID.artDecoGold,
  COLORSET_ID.smokeVelvet,
  COLORSET_ID.emeraldBooth,
  COLORSET_ID.champagneAsh,
  COLORSET_ID.streetNeon,
  COLORSET_ID.asphaltBold,
  COLORSET_ID.sunsetPop,
  COLORSET_ID.ketchupMustard,
  COLORSET_ID.pideCream,
  COLORSET_ID.mustardPaper,
  COLORSET_ID.tramAsphalt,
  COLORSET_ID.ketchupSignal,
  COLORSET_ID.pommesGold,
  COLORSET_ID.grillChar,
  COLORSET_ID.hummusPaper,
  COLORSET_ID.oreganoNight,
  COLORSET_ID.lemonStall,
  COLORSET_ID.sesameCream,
  COLORSET_ID.zaatarDusk,
  COLORSET_ID.coastalBlue,
  COLORSET_ID.sandDune,
  COLORSET_ID.forestTrail,
  COLORSET_ID.canyonDust,
  COLORSET_ID.arcticFog,
  COLORSET_ID.slateCounter,
  COLORSET_ID.midnightNori,
  COLORSET_ID.wasabiPaper,
  COLORSET_ID.shariIvory,
  COLORSET_ID.cedarPlank,
  COLORSET_ID.herbMist,
  COLORSET_ID.clayBroth,
  COLORSET_ID.midnightLotus,
  COLORSET_ID.bananaSteam,
  COLORSET_ID.saffronBowl,
  COLORSET_ID.ceramicDawn,
  COLORSET_ID.cayenneStripe,
  COLORSET_ID.inkShop,
  COLORSET_ID.groutLime,
  COLORSET_ID.monsoonBlue,
  COLORSET_ID.lacquerPaper,
  COLORSET_ID.inkWash,
  COLORSET_ID.emberLantern,
  COLORSET_ID.celadonCourt,
  COLORSET_ID.plumSilk,
] as const;

export type ColorSetId = (typeof COLORSET_ID_KEYS)[number];

/**
 * Renamed / retired color-set slugs → current `COLORSET_ID` values.
 * Stored project configs keep loading after catalog renames.
 */
export const LEGACY_COLORSET_ID_ALIASES = {
  yuzu_foam: COLORSET_ID.shariIvory,
  fern_lime: COLORSET_ID.apricotTrellis,
  mint_stall: COLORSET_ID.pideCream,
} as const satisfies Record<string, ColorSetId>;

/** Map legacy color-set ids onto current catalog slugs; pass-through otherwise. */
export function normalizeColorSetId(value: string): string {
  const legacy =
    LEGACY_COLORSET_ID_ALIASES[value as keyof typeof LEGACY_COLORSET_ID_ALIASES];
  return legacy ?? value;
}

export const ColorSetIdSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeColorSetId(value) : value),
  z.enum(COLORSET_ID_KEYS),
);

/**
 * Nested registry: block **types** (tier `allowedBlocks`) vs **presentations**
 * (`LayoutBlock.registryBlockKey` + theme `blockPresentations`).
 */
export const REGISTRY_BLOCK = {
  type: {
    navigation: 'Navigation',
    hero: 'Hero',
    staticMenu: 'Static_Menu',
    dynamicMenu: 'Dynamic_Menu',
    reservation: 'Reservation',
    chefStory: 'Chef_Story',
    /** Kitchen / house people — name, role, optional photo. Not philosophy. */
    kitchenTeam: 'Kitchen_Team',
    /** Curated photos of the room, counter, terrace, truck, or shop. */
    imageGallery: 'Image_Gallery',
    tastingTimeline: 'Tasting_Timeline',
    allergyForm: 'Allergy_Form',
    footer: 'Footer',
    openingHours: 'Opening_Hours',
    addressMap: 'Address_Map',
    liveBakeryCounter: 'Live_Bakery_Counter',
    socialFeed: 'Social_Feed',
    coffeeSubscription: 'Coffee_Subscription',
    preOrderPickup: 'Pre_Order_Pickup',
    drinksList: 'Drinks_List',
    cocktailSpotlight: 'Cocktail_Spotlight',
    privatePartyInquiry: 'Private_Party_Inquiry',
    liveTapList: 'Live_Tap_List',
    vipTableBooking: 'VIP_Table_Booking',
    staticLocation: 'Static_Location',
    simpleMenuStrip: 'Simple_Menu_Strip',
    liveGps: 'Live_GPS',
    weeklySchedule: 'Weekly_Schedule',
    popUpTicket: 'PopUp_Ticket',
    cateringInquiry: 'Catering_Inquiry',
    legalImprint: 'Legal_Imprint',
    legalPrivacy: 'Legal_Privacy',
    contact: 'Contact',
    /** Guest Q&A accordion / ledger — often on Catering / About. */
    faq: 'FAQ',
    /** Global chrome — scroll-to-top after leaving the first viewport. */
    backToTop: 'Back_To_Top',
    /** Global chrome — floating phone + social channel bar. */
    contactChannels: 'Contact_Channels',
    /** Global chrome — thin utility strip above navigation. */
    utilityBar: 'Utility_Bar',
    /** Global chrome — temporary site-wide notice above navigation. */
    announcementBar: 'Announcement_Bar',
  },
  presentation: {
    /**
     * Arrangement = Rail (logo left). Canonical nav arrangement key —
     * chrome lives in `content.navPresentation`, not in the registry key.
     */
    navigationRail: 'Navigation_Rail',
    /** @deprecated Chrome-as-key alias → Rail + classic. Prefer navigationRail. */
    navigationClassic: 'Navigation_Classic',
    /** @deprecated Chrome-as-key alias → Rail + overlay. Prefer navigationRail. */
    navigationOverlay: 'Navigation_Overlay',
    /** @deprecated Chrome-as-key alias → Rail + quiet. Prefer navigationRail. */
    navigationQuiet: 'Navigation_Quiet',
    /** @deprecated Chrome-as-key alias → Rail + frosted. Prefer navigationRail. */
    navigationFrosted: 'Navigation_Frosted',
    /** @deprecated Chrome-as-key alias → Rail + conversion. Prefer navigationRail. */
    navigationConversion: 'Navigation_Conversion',
    /** @deprecated Chrome-as-key alias → Rail + whisper. Prefer navigationRail. */
    navigationWhisper: 'Navigation_Whisper',
    /** @deprecated Chrome-as-key alias → Rail + editorial. Prefer navigationRail. */
    navigationEditorial: 'Navigation_Editorial',
    /** Arrangement = Compass (logo center); chrome via content.navPresentation. */
    navigationCompass: 'Navigation_Compass',
    /**
     * Arrangement = Curtain — Rail bar topology + full-bleed phone open ritual.
     * Chrome via content.navPresentation.
     */
    navigationCurtain: 'Navigation_Curtain',
    /**
     * Arrangement = Ledger — Rail bar topology + bottom-sheet phone open ritual.
     * Chrome via content.navPresentation.
     */
    navigationLedger: 'Navigation_Ledger',
    /** @deprecated Locale is universal; Harbor was brand-left + centered links only. */
    navigationHarbor: 'Navigation_Harbor',
    /**
     * Arrangement = Monogram — Rail bar topology + single-mark toggle + narrow
     * edge panel on phone. (Revived; no longer auto-normalized to Rail.)
     */
    navigationMonogram: 'Navigation_Monogram',
    /**
     * Arrangement = Signal — Rail bar topology + phone sheet with gastronomy
     * social connections (Instagram / Facebook / … from Local Presence).
     */
    navigationSignal: 'Navigation_Signal',
    /** @deprecated Mega-menu bars are rare for small restaurant sites. */
    navigationGallery: 'Navigation_Gallery',
    /** @deprecated Prefer navigationRail — kept for existing content rows. */
    navigationHeader: 'Navigation_Header',
    heroEditorialSplit: 'Hero_Editorial_Split',
    heroVideoAtmosphere: 'Hero_Video_Atmosphere',
    heroCoffeeSpotlight: 'Hero_Coffee_Spotlight',
    heroFullBleedImage: 'Hero_Full_Bleed_Image',
    heroStandard: 'Hero_Standard',
    heroQuietLuxury: 'Hero_Quiet_Luxury',
    /**
     * Full-bleed counter photo with floating house seal — bridges Monolith
     * ceremony and cinematic immersion (not Quiet Luxury split, not Full-Bleed side veil).
     */
    heroCounterBleed: 'Hero_Counter_Bleed',
    heroUtilityReservation: 'Hero_Utility_Reservation',
    heroCollageStory: 'Hero_Collage_Story',
    /**
     * Quiet multi-frame full-bleed — 2–3 photos crossfade (not static Full-Bleed,
     * not Video Atmosphere).
     */
    heroReelFade: 'Hero_Reel_Fade',
    /**
     * Vietnam / broth wow — full-bleed plate with rising steam veil;
     * copy sits in a soft vapor pocket (not a flat gradient bar).
     */
    heroBrothSteam: 'Hero_Broth_Steam',
    staticMenuGrid: 'Static_Menu_Grid',
    /** Printed Speisekarte card + optional PDF download. */
    staticMenuPdfCard: 'Static_Menu_Pdf_Card',
    /** Category bands with photography. */
    staticMenuCategoryGallery: 'Static_Menu_Category_Gallery',
    /** Photo-forward dish cards. */
    staticMenuDishCards: 'Static_Menu_Dish_Cards',
    /** Fine-dining single-measure list with price leaders. */
    staticMenuEditorialBoard: 'Static_Menu_Editorial_Board',
    /**
     * Imbiss / street Speisekarte — category tile board (photo optional),
     * one Abschnitt open at a time; dishes stay typographic.
     */
    staticMenuStreetBoard: 'Static_Menu_Street_Board',
    /**
     * Full Speisekarte — category chips; selecting a chip shows that category’s items.
     * Compact filter chrome (not Street Board photo tiles).
     */
    staticMenuCategoryChips: 'Static_Menu_Category_Chips',
    /**
     * Highlight / curated list — cinematic promenade (no category chrome).
     * Favorites, specials, lunch, happy hour, today… — not the full Speisekarte.
     */
    staticMenuHighlightStrip: 'Static_Menu_Highlight_Strip',
    /** Continuous name·price marquee under the hero. */
    staticMenuHighlightMarquee: 'Static_Menu_Highlight_Marquee',
    /** One oversized lead plate + supporting chips. */
    staticMenuHighlightHeroPlate: 'Static_Menu_Highlight_Hero_Plate',
    /** Overlapping polaroid-style stack. */
    staticMenuHighlightZine: 'Static_Menu_Highlight_Zine',
    /** Chalkboard / limited-time board (lunch, happy hour). */
    staticMenuHighlightChalkboard: 'Static_Menu_Highlight_Chalkboard',
    /** Numbered chef’s picks, typography-led. */
    staticMenuHighlightChefsCut: 'Static_Menu_Highlight_Chefs_Cut',
    /** “Today” kicker + compact dish list with shared wash. */
    staticMenuHighlightTodaySplit: 'Static_Menu_Highlight_Today_Split',
    /** Price-first deal tiles. */
    staticMenuHighlightPriceTiles: 'Static_Menu_Highlight_Price_Tiles',
    /** Photo dish cards for curated highlights — flat grid, no Hinweise. */
    staticMenuHighlightDishCards: 'Static_Menu_Highlight_Dish_Cards',
    dynamicMenuFlow: 'Dynamic_Menu_Flow',
    reservationFormEmbed: 'Reservation_Form_Embed',
    /** Fine-dining inquiry — editorial form chrome (no app-panel surface). */
    reservationEditorialForm: 'Reservation_Editorial_Form',
    /** Deep-link / CTA to an external booker (Resmio, DISH, OpenTable, …). */
    reservationThirdPartyBook: 'Reservation_Third_Party_Book',
    /** Messenger CTA (WhatsApp, Facebook Messenger, Instagram DM, …). */
    reservationMessengerBook: 'Reservation_Messenger_Book',
    /** Phone CTA (`tel:`). */
    reservationCallBook: 'Reservation_Call_Book',
    /** Classic portrait split — copy + chef portrait. */
    chefStoryBlock: 'Chef_Story_Block',
    /** Quote / philosophy led; portrait secondary. */
    chefStoryQuoteLed: 'Chef_Story_Quote_Led',
    /** Horizontal kitchen band — compact story strip. */
    chefStoryKitchenStrip: 'Chef_Story_Kitchen_Strip',
    /** Quiet masthead, then notes — label, one paragraph, optional small photo. */
    chefStoryNotes: 'Chef_Story_Notes',
    /** Team — large pass / lead photo beside the roster. */
    kitchenTeamLead: 'Kitchen_Team_Lead',
    /** Team — equal portrait cards. */
    kitchenTeamRow: 'Kitchen_Team_Row',
    /** Team — compact photo + short name list. */
    kitchenTeamBand: 'Kitchen_Team_Band',
    /** Bento photo wall — lead frame plus companions. */
    imageGalleryMosaic: 'Image_Gallery_Mosaic',
    /** Column masonry — native heights, Unsplash-style waterfall. */
    imageGalleryMasonry: 'Image_Gallery_Masonry',
    /** Horizontal snap walk; the next frame peeks on a phone. */
    imageGalleryFilmstrip: 'Image_Gallery_Filmstrip',
    /** One stage photograph plus a thumbnail rail. */
    imageGallerySpotlight: 'Image_Gallery_Spotlight',
    /** Alternating rows — a quiet tour of the place. */
    imageGalleryLedger: 'Image_Gallery_Ledger',
    tastingMenuCourseTimeline: 'Tasting_Menu_Course_Timeline',
    /** Ritual cards — pairing seals sit with the course procession. */
    tastingMenuPairingCards: 'Tasting_Menu_Pairing_Cards',
    allergyCaptureSecureForm: 'Allergy_Capture_Secure_Form',
    footerStandard: 'Footer_Standard',
    footerColophon: 'Footer_Colophon',
    footerDirectory: 'Footer_Directory',
    footerUtility: 'Footer_Utility',
    simpleOpeningHours: 'Simple_Opening_Hours',
    addressMapBlock: 'Address_Map_Block',
    liveBakeryCounterList: 'Live_Bakery_Counter_List',
    socialFeedGrid: 'Social_Feed_Grid',
    coffeeSubscriptionMatrix: 'Coffee_Subscription_Matrix',
    preOrderPickupForm: 'Pre_Order_Pickup_Form',
    drinksListStatic: 'Drinks_List_Static',
    /** Sommelier ledger — regions as ruled bands, glass price on the right. */
    drinksListCellar: 'Drinks_List_Cellar',
    /** Wine sheet — one prose card, single column, centered masthead. */
    drinksListSheet: 'Drinks_List_Sheet',
    dynamicCocktailSpotlight: 'Dynamic_Cocktail_Spotlight',
    privatePartyInquiryForm: 'Private_Party_Inquiry_Form',
    /** Chef’s table split — room photograph + quiet inquiry card. */
    privatePartyInquirySalon: 'Private_Party_Inquiry_Salon',
    liveTapListSync: 'Live_Tap_List_Sync',
    vipTableBookingStrip: 'VIP_Table_Booking_Strip',
    staticLocationText: 'Static_Location_Text',
    /** Wide stop board for scenic / nomad trucks (not the narrow text column). */
    staticLocationStop: 'Static_Location_Stop',
    simpleMenuStrip: 'Simple_Menu_Strip',
    /** Wide chalkboard menu for nomad trucks (not the narrow strip). */
    simpleMenuBoard: 'Simple_Menu_Board',
    liveGpsLocationEmbed: 'Live_GPS_Location_Embed',
    weeklyScheduleRouteStrip: 'Weekly_Schedule_Route_Strip',
    /** Day tiles across the page measure (not the narrow route strip). */
    weeklyScheduleRouteBoard: 'Weekly_Schedule_Route_Board',
    popUpTicketPurchaseCard: 'PopUp_Ticket_Purchase_Card',
    cateringInquiryForm: 'Catering_Inquiry_Form',
    contactVisit: 'Contact_Visit',
    /** Message-first: form, then a thin call / mail / today strip. */
    contactDesk: 'Contact_Desk',
    /** One ruled house card: address, way, phone, hours. */
    contactLedger: 'Contact_Ledger',
    /** Full-bleed photo with three chips; the message overlaps the edge. */
    contactThreshold: 'Contact_Threshold',
    legalImprintBlock: 'Legal_Imprint_Block',
    legalPrivacyBlock: 'Legal_Privacy_Block',
    /** Native `<details>` accordion — first answered item open. */
    faqAccordion: 'FAQ_Accordion',
    /** All answers visible in ruled rows. */
    faqLedger: 'FAQ_Ledger',
    /** Corner disc — filled brand circle, bottom-right. */
    backToTopCorner: 'BackToTop_Corner',
    /** Transparent icon — larger glyph only, sits lightly over content. */
    backToTopPill: 'BackToTop_Pill',
    /** Vertical rail — all channels visible, hugs the right edge. */
    contactChannelsRail: 'ContactChannels_Rail',
    /** One FAB; channels expand on click / hover (minimal content occlusion). */
    contactChannelsFab: 'ContactChannels_Fab',
    /** Utility strip — document flow; scrolls away with the page. */
    utilityBarStatic: 'UtilityBar_Static',
    /** Utility strip — sticky under the viewport top while scrolling. */
    utilityBarSticky: 'UtilityBar_Sticky',
    /** Utility strip — rotates one item at a time. */
    utilityBarCarousel: 'UtilityBar_Carousel',
    /** Utility strip — teaser chevron expands into the full band. */
    utilityBarCollapse: 'UtilityBar_Collapse',
    /** Announcement — sticky top band with message, optional CTA, tone, schedule. */
    announcementBarBand: 'AnnouncementBar_Band',
  },
} as const;

export const REGISTRY_BLOCK_TYPE_VALUES = [
  REGISTRY_BLOCK.type.navigation,
  REGISTRY_BLOCK.type.hero,
  REGISTRY_BLOCK.type.staticMenu,
  REGISTRY_BLOCK.type.dynamicMenu,
  REGISTRY_BLOCK.type.reservation,
  REGISTRY_BLOCK.type.chefStory,
  REGISTRY_BLOCK.type.kitchenTeam,
  REGISTRY_BLOCK.type.imageGallery,
  REGISTRY_BLOCK.type.tastingTimeline,
  REGISTRY_BLOCK.type.allergyForm,
  REGISTRY_BLOCK.type.footer,
  REGISTRY_BLOCK.type.openingHours,
  REGISTRY_BLOCK.type.addressMap,
  REGISTRY_BLOCK.type.liveBakeryCounter,
  REGISTRY_BLOCK.type.socialFeed,
  REGISTRY_BLOCK.type.coffeeSubscription,
  REGISTRY_BLOCK.type.preOrderPickup,
  REGISTRY_BLOCK.type.drinksList,
  REGISTRY_BLOCK.type.cocktailSpotlight,
  REGISTRY_BLOCK.type.privatePartyInquiry,
  REGISTRY_BLOCK.type.liveTapList,
  REGISTRY_BLOCK.type.vipTableBooking,
  REGISTRY_BLOCK.type.staticLocation,
  REGISTRY_BLOCK.type.simpleMenuStrip,
  REGISTRY_BLOCK.type.liveGps,
  REGISTRY_BLOCK.type.weeklySchedule,
  REGISTRY_BLOCK.type.popUpTicket,
  REGISTRY_BLOCK.type.cateringInquiry,
  REGISTRY_BLOCK.type.legalImprint,
  REGISTRY_BLOCK.type.legalPrivacy,
  REGISTRY_BLOCK.type.contact,
  REGISTRY_BLOCK.type.faq,
  REGISTRY_BLOCK.type.backToTop,
  REGISTRY_BLOCK.type.contactChannels,
  REGISTRY_BLOCK.type.utilityBar,
  REGISTRY_BLOCK.type.announcementBar,
] as const;

export type RegistryBlockType = (typeof REGISTRY_BLOCK_TYPE_VALUES)[number];
export const RegistryBlockTypeSchema = z.enum(REGISTRY_BLOCK_TYPE_VALUES);

export const REGISTRY_BLOCK_PRESENTATION_VALUES = [
  REGISTRY_BLOCK.presentation.navigationRail,
  REGISTRY_BLOCK.presentation.navigationClassic,
  REGISTRY_BLOCK.presentation.navigationOverlay,
  REGISTRY_BLOCK.presentation.navigationQuiet,
  REGISTRY_BLOCK.presentation.navigationFrosted,
  REGISTRY_BLOCK.presentation.navigationConversion,
  REGISTRY_BLOCK.presentation.navigationWhisper,
  REGISTRY_BLOCK.presentation.navigationEditorial,
  REGISTRY_BLOCK.presentation.navigationCompass,
  REGISTRY_BLOCK.presentation.navigationCurtain,
  REGISTRY_BLOCK.presentation.navigationLedger,
  REGISTRY_BLOCK.presentation.navigationHarbor,
  REGISTRY_BLOCK.presentation.navigationMonogram,
  REGISTRY_BLOCK.presentation.navigationSignal,
  REGISTRY_BLOCK.presentation.navigationGallery,
  REGISTRY_BLOCK.presentation.navigationHeader,
  REGISTRY_BLOCK.presentation.heroEditorialSplit,
  REGISTRY_BLOCK.presentation.heroVideoAtmosphere,
  REGISTRY_BLOCK.presentation.heroCoffeeSpotlight,
  REGISTRY_BLOCK.presentation.heroFullBleedImage,
  REGISTRY_BLOCK.presentation.heroStandard,
  REGISTRY_BLOCK.presentation.heroQuietLuxury,
  REGISTRY_BLOCK.presentation.heroCounterBleed,
  REGISTRY_BLOCK.presentation.heroUtilityReservation,
  REGISTRY_BLOCK.presentation.heroCollageStory,
  REGISTRY_BLOCK.presentation.heroReelFade,
  REGISTRY_BLOCK.presentation.heroBrothSteam,
  REGISTRY_BLOCK.presentation.staticMenuGrid,
  REGISTRY_BLOCK.presentation.staticMenuPdfCard,
  REGISTRY_BLOCK.presentation.staticMenuCategoryGallery,
  REGISTRY_BLOCK.presentation.staticMenuDishCards,
  REGISTRY_BLOCK.presentation.staticMenuEditorialBoard,
  REGISTRY_BLOCK.presentation.staticMenuStreetBoard,
  REGISTRY_BLOCK.presentation.staticMenuCategoryChips,
  REGISTRY_BLOCK.presentation.staticMenuHighlightStrip,
  REGISTRY_BLOCK.presentation.staticMenuHighlightMarquee,
  REGISTRY_BLOCK.presentation.staticMenuHighlightHeroPlate,
  REGISTRY_BLOCK.presentation.staticMenuHighlightZine,
  REGISTRY_BLOCK.presentation.staticMenuHighlightChalkboard,
  REGISTRY_BLOCK.presentation.staticMenuHighlightChefsCut,
  REGISTRY_BLOCK.presentation.staticMenuHighlightTodaySplit,
  REGISTRY_BLOCK.presentation.staticMenuHighlightPriceTiles,
  REGISTRY_BLOCK.presentation.staticMenuHighlightDishCards,
  REGISTRY_BLOCK.presentation.dynamicMenuFlow,
  REGISTRY_BLOCK.presentation.reservationFormEmbed,
  REGISTRY_BLOCK.presentation.reservationEditorialForm,
  REGISTRY_BLOCK.presentation.reservationThirdPartyBook,
  REGISTRY_BLOCK.presentation.reservationMessengerBook,
  REGISTRY_BLOCK.presentation.reservationCallBook,
  REGISTRY_BLOCK.presentation.chefStoryBlock,
  REGISTRY_BLOCK.presentation.chefStoryQuoteLed,
  REGISTRY_BLOCK.presentation.chefStoryKitchenStrip,
  REGISTRY_BLOCK.presentation.chefStoryNotes,
  REGISTRY_BLOCK.presentation.kitchenTeamLead,
  REGISTRY_BLOCK.presentation.kitchenTeamRow,
  REGISTRY_BLOCK.presentation.kitchenTeamBand,
  REGISTRY_BLOCK.presentation.imageGalleryMosaic,
  REGISTRY_BLOCK.presentation.imageGalleryMasonry,
  REGISTRY_BLOCK.presentation.imageGalleryFilmstrip,
  REGISTRY_BLOCK.presentation.imageGallerySpotlight,
  REGISTRY_BLOCK.presentation.imageGalleryLedger,
  REGISTRY_BLOCK.presentation.tastingMenuCourseTimeline,
  REGISTRY_BLOCK.presentation.tastingMenuPairingCards,
  REGISTRY_BLOCK.presentation.allergyCaptureSecureForm,
  REGISTRY_BLOCK.presentation.footerStandard,
  REGISTRY_BLOCK.presentation.footerColophon,
  REGISTRY_BLOCK.presentation.footerDirectory,
  REGISTRY_BLOCK.presentation.footerUtility,
  REGISTRY_BLOCK.presentation.simpleOpeningHours,
  REGISTRY_BLOCK.presentation.addressMapBlock,
  REGISTRY_BLOCK.presentation.liveBakeryCounterList,
  REGISTRY_BLOCK.presentation.socialFeedGrid,
  REGISTRY_BLOCK.presentation.coffeeSubscriptionMatrix,
  REGISTRY_BLOCK.presentation.preOrderPickupForm,
  REGISTRY_BLOCK.presentation.drinksListStatic,
  REGISTRY_BLOCK.presentation.drinksListCellar,
  REGISTRY_BLOCK.presentation.drinksListSheet,
  REGISTRY_BLOCK.presentation.dynamicCocktailSpotlight,
  REGISTRY_BLOCK.presentation.privatePartyInquiryForm,
  REGISTRY_BLOCK.presentation.privatePartyInquirySalon,
  REGISTRY_BLOCK.presentation.liveTapListSync,
  REGISTRY_BLOCK.presentation.vipTableBookingStrip,
  REGISTRY_BLOCK.presentation.staticLocationText,
  REGISTRY_BLOCK.presentation.staticLocationStop,
  REGISTRY_BLOCK.presentation.simpleMenuStrip,
  REGISTRY_BLOCK.presentation.simpleMenuBoard,
  REGISTRY_BLOCK.presentation.liveGpsLocationEmbed,
  REGISTRY_BLOCK.presentation.weeklyScheduleRouteStrip,
  REGISTRY_BLOCK.presentation.weeklyScheduleRouteBoard,
  REGISTRY_BLOCK.presentation.popUpTicketPurchaseCard,
  REGISTRY_BLOCK.presentation.cateringInquiryForm,
  REGISTRY_BLOCK.presentation.legalImprintBlock,
  REGISTRY_BLOCK.presentation.legalPrivacyBlock,
  REGISTRY_BLOCK.presentation.contactVisit,
  REGISTRY_BLOCK.presentation.contactDesk,
  REGISTRY_BLOCK.presentation.contactLedger,
  REGISTRY_BLOCK.presentation.contactThreshold,
  REGISTRY_BLOCK.presentation.faqAccordion,
  REGISTRY_BLOCK.presentation.faqLedger,
  REGISTRY_BLOCK.presentation.backToTopCorner,
  REGISTRY_BLOCK.presentation.backToTopPill,
  REGISTRY_BLOCK.presentation.contactChannelsRail,
  REGISTRY_BLOCK.presentation.contactChannelsFab,
  REGISTRY_BLOCK.presentation.utilityBarStatic,
  REGISTRY_BLOCK.presentation.utilityBarSticky,
  REGISTRY_BLOCK.presentation.utilityBarCarousel,
  REGISTRY_BLOCK.presentation.utilityBarCollapse,
  REGISTRY_BLOCK.presentation.announcementBarBand,
] as const;

/** Concrete UI variant key — stored on `LayoutBlock.registryBlockKey`. */
export type RegistryBlockPresentation = (typeof REGISTRY_BLOCK_PRESENTATION_VALUES)[number];
export type RegistryBlockKey = RegistryBlockPresentation;
export const REGISTRY_BLOCK_KEY_VALUES = REGISTRY_BLOCK_PRESENTATION_VALUES;
export const RegistryBlockKeySchema = z.enum(REGISTRY_BLOCK_PRESENTATION_VALUES);
export const RegistryBlockPresentationSchema = RegistryBlockKeySchema;

/** Legal types — always entitled on every vertical/tier. */
export const ALWAYS_ALLOWED_BLOCK_TYPES = [
  REGISTRY_BLOCK.type.legalImprint,
  REGISTRY_BLOCK.type.legalPrivacy,
] as const satisfies readonly RegistryBlockType[];

export const PRESENTATION_TO_BLOCK_TYPE: Readonly<
  Record<RegistryBlockPresentation, RegistryBlockType>
> = {
  [REGISTRY_BLOCK.presentation.navigationRail]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationClassic]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationOverlay]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationQuiet]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationFrosted]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationConversion]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationWhisper]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationEditorial]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationCompass]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationCurtain]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationLedger]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationHarbor]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationMonogram]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationSignal]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationGallery]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.navigationHeader]: REGISTRY_BLOCK.type.navigation,
  [REGISTRY_BLOCK.presentation.heroEditorialSplit]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroVideoAtmosphere]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroCoffeeSpotlight]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroFullBleedImage]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroStandard]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroQuietLuxury]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroCounterBleed]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroUtilityReservation]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroCollageStory]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroReelFade]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.heroBrothSteam]: REGISTRY_BLOCK.type.hero,
  [REGISTRY_BLOCK.presentation.staticMenuGrid]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuPdfCard]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuCategoryGallery]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuDishCards]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuEditorialBoard]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuStreetBoard]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuCategoryChips]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightStrip]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightMarquee]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightHeroPlate]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightZine]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightChalkboard]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightChefsCut]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightTodaySplit]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightPriceTiles]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.staticMenuHighlightDishCards]: REGISTRY_BLOCK.type.staticMenu,
  [REGISTRY_BLOCK.presentation.dynamicMenuFlow]: REGISTRY_BLOCK.type.dynamicMenu,
  [REGISTRY_BLOCK.presentation.reservationFormEmbed]: REGISTRY_BLOCK.type.reservation,
  [REGISTRY_BLOCK.presentation.reservationEditorialForm]: REGISTRY_BLOCK.type.reservation,
  [REGISTRY_BLOCK.presentation.reservationThirdPartyBook]: REGISTRY_BLOCK.type.reservation,
  [REGISTRY_BLOCK.presentation.reservationMessengerBook]: REGISTRY_BLOCK.type.reservation,
  [REGISTRY_BLOCK.presentation.reservationCallBook]: REGISTRY_BLOCK.type.reservation,
  [REGISTRY_BLOCK.presentation.chefStoryBlock]: REGISTRY_BLOCK.type.chefStory,
  [REGISTRY_BLOCK.presentation.chefStoryQuoteLed]: REGISTRY_BLOCK.type.chefStory,
  [REGISTRY_BLOCK.presentation.chefStoryKitchenStrip]: REGISTRY_BLOCK.type.chefStory,
  [REGISTRY_BLOCK.presentation.imageGalleryMosaic]: REGISTRY_BLOCK.type.imageGallery,
  [REGISTRY_BLOCK.presentation.chefStoryNotes]: REGISTRY_BLOCK.type.chefStory,
  [REGISTRY_BLOCK.presentation.kitchenTeamLead]: REGISTRY_BLOCK.type.kitchenTeam,
  [REGISTRY_BLOCK.presentation.kitchenTeamRow]: REGISTRY_BLOCK.type.kitchenTeam,
  [REGISTRY_BLOCK.presentation.kitchenTeamBand]: REGISTRY_BLOCK.type.kitchenTeam,
  [REGISTRY_BLOCK.presentation.imageGalleryMasonry]: REGISTRY_BLOCK.type.imageGallery,
  [REGISTRY_BLOCK.presentation.imageGalleryFilmstrip]: REGISTRY_BLOCK.type.imageGallery,
  [REGISTRY_BLOCK.presentation.imageGallerySpotlight]: REGISTRY_BLOCK.type.imageGallery,
  [REGISTRY_BLOCK.presentation.imageGalleryLedger]: REGISTRY_BLOCK.type.imageGallery,
  [REGISTRY_BLOCK.presentation.tastingMenuCourseTimeline]: REGISTRY_BLOCK.type.tastingTimeline,
  [REGISTRY_BLOCK.presentation.tastingMenuPairingCards]: REGISTRY_BLOCK.type.tastingTimeline,
  [REGISTRY_BLOCK.presentation.allergyCaptureSecureForm]: REGISTRY_BLOCK.type.allergyForm,
  [REGISTRY_BLOCK.presentation.footerStandard]: REGISTRY_BLOCK.type.footer,
  [REGISTRY_BLOCK.presentation.footerColophon]: REGISTRY_BLOCK.type.footer,
  [REGISTRY_BLOCK.presentation.footerDirectory]: REGISTRY_BLOCK.type.footer,
  [REGISTRY_BLOCK.presentation.footerUtility]: REGISTRY_BLOCK.type.footer,
  [REGISTRY_BLOCK.presentation.simpleOpeningHours]: REGISTRY_BLOCK.type.openingHours,
  [REGISTRY_BLOCK.presentation.addressMapBlock]: REGISTRY_BLOCK.type.addressMap,
  [REGISTRY_BLOCK.presentation.liveBakeryCounterList]: REGISTRY_BLOCK.type.liveBakeryCounter,
  [REGISTRY_BLOCK.presentation.socialFeedGrid]: REGISTRY_BLOCK.type.socialFeed,
  [REGISTRY_BLOCK.presentation.coffeeSubscriptionMatrix]: REGISTRY_BLOCK.type.coffeeSubscription,
  [REGISTRY_BLOCK.presentation.preOrderPickupForm]: REGISTRY_BLOCK.type.preOrderPickup,
  [REGISTRY_BLOCK.presentation.drinksListStatic]: REGISTRY_BLOCK.type.drinksList,
  [REGISTRY_BLOCK.presentation.drinksListCellar]: REGISTRY_BLOCK.type.drinksList,
  [REGISTRY_BLOCK.presentation.drinksListSheet]: REGISTRY_BLOCK.type.drinksList,
  [REGISTRY_BLOCK.presentation.dynamicCocktailSpotlight]: REGISTRY_BLOCK.type.cocktailSpotlight,
  [REGISTRY_BLOCK.presentation.privatePartyInquiryForm]: REGISTRY_BLOCK.type.privatePartyInquiry,
  [REGISTRY_BLOCK.presentation.privatePartyInquirySalon]: REGISTRY_BLOCK.type.privatePartyInquiry,
  [REGISTRY_BLOCK.presentation.liveTapListSync]: REGISTRY_BLOCK.type.liveTapList,
  [REGISTRY_BLOCK.presentation.vipTableBookingStrip]: REGISTRY_BLOCK.type.vipTableBooking,
  [REGISTRY_BLOCK.presentation.staticLocationText]: REGISTRY_BLOCK.type.staticLocation,
  [REGISTRY_BLOCK.presentation.staticLocationStop]: REGISTRY_BLOCK.type.staticLocation,
  [REGISTRY_BLOCK.presentation.simpleMenuStrip]: REGISTRY_BLOCK.type.simpleMenuStrip,
  [REGISTRY_BLOCK.presentation.simpleMenuBoard]: REGISTRY_BLOCK.type.simpleMenuStrip,
  [REGISTRY_BLOCK.presentation.liveGpsLocationEmbed]: REGISTRY_BLOCK.type.liveGps,
  [REGISTRY_BLOCK.presentation.weeklyScheduleRouteStrip]: REGISTRY_BLOCK.type.weeklySchedule,
  [REGISTRY_BLOCK.presentation.weeklyScheduleRouteBoard]: REGISTRY_BLOCK.type.weeklySchedule,
  [REGISTRY_BLOCK.presentation.popUpTicketPurchaseCard]: REGISTRY_BLOCK.type.popUpTicket,
  [REGISTRY_BLOCK.presentation.cateringInquiryForm]: REGISTRY_BLOCK.type.cateringInquiry,
  [REGISTRY_BLOCK.presentation.legalImprintBlock]: REGISTRY_BLOCK.type.legalImprint,
  [REGISTRY_BLOCK.presentation.legalPrivacyBlock]: REGISTRY_BLOCK.type.legalPrivacy,
  [REGISTRY_BLOCK.presentation.contactVisit]: REGISTRY_BLOCK.type.contact,
  [REGISTRY_BLOCK.presentation.contactDesk]: REGISTRY_BLOCK.type.contact,
  [REGISTRY_BLOCK.presentation.contactLedger]: REGISTRY_BLOCK.type.contact,
  [REGISTRY_BLOCK.presentation.contactThreshold]: REGISTRY_BLOCK.type.contact,
  [REGISTRY_BLOCK.presentation.faqAccordion]: REGISTRY_BLOCK.type.faq,
  [REGISTRY_BLOCK.presentation.faqLedger]: REGISTRY_BLOCK.type.faq,
  [REGISTRY_BLOCK.presentation.backToTopCorner]: REGISTRY_BLOCK.type.backToTop,
  [REGISTRY_BLOCK.presentation.backToTopPill]: REGISTRY_BLOCK.type.backToTop,
  [REGISTRY_BLOCK.presentation.contactChannelsRail]: REGISTRY_BLOCK.type.contactChannels,
  [REGISTRY_BLOCK.presentation.contactChannelsFab]: REGISTRY_BLOCK.type.contactChannels,
  [REGISTRY_BLOCK.presentation.utilityBarStatic]: REGISTRY_BLOCK.type.utilityBar,
  [REGISTRY_BLOCK.presentation.utilityBarSticky]: REGISTRY_BLOCK.type.utilityBar,
  [REGISTRY_BLOCK.presentation.utilityBarCarousel]: REGISTRY_BLOCK.type.utilityBar,
  [REGISTRY_BLOCK.presentation.utilityBarCollapse]: REGISTRY_BLOCK.type.utilityBar,
  [REGISTRY_BLOCK.presentation.announcementBarBand]: REGISTRY_BLOCK.type.announcementBar,
};

/** Fallback presentation when theme mix omits a type. */
export const DEFAULT_PRESENTATION_BY_TYPE: Readonly<
  Record<RegistryBlockType, RegistryBlockPresentation>
> = {
  [REGISTRY_BLOCK.type.navigation]: REGISTRY_BLOCK.presentation.navigationRail,
  [REGISTRY_BLOCK.type.hero]: REGISTRY_BLOCK.presentation.heroEditorialSplit,
  [REGISTRY_BLOCK.type.staticMenu]: REGISTRY_BLOCK.presentation.staticMenuGrid,
  [REGISTRY_BLOCK.type.dynamicMenu]: REGISTRY_BLOCK.presentation.dynamicMenuFlow,
  [REGISTRY_BLOCK.type.reservation]: REGISTRY_BLOCK.presentation.reservationFormEmbed,
  [REGISTRY_BLOCK.type.chefStory]: REGISTRY_BLOCK.presentation.chefStoryBlock,
  [REGISTRY_BLOCK.type.imageGallery]: REGISTRY_BLOCK.presentation.imageGalleryMosaic,
  [REGISTRY_BLOCK.type.tastingTimeline]: REGISTRY_BLOCK.presentation.tastingMenuCourseTimeline,
  [REGISTRY_BLOCK.type.kitchenTeam]: REGISTRY_BLOCK.presentation.kitchenTeamLead,
  [REGISTRY_BLOCK.type.allergyForm]: REGISTRY_BLOCK.presentation.allergyCaptureSecureForm,
  [REGISTRY_BLOCK.type.footer]: REGISTRY_BLOCK.presentation.footerStandard,
  [REGISTRY_BLOCK.type.openingHours]: REGISTRY_BLOCK.presentation.simpleOpeningHours,
  [REGISTRY_BLOCK.type.addressMap]: REGISTRY_BLOCK.presentation.addressMapBlock,
  [REGISTRY_BLOCK.type.liveBakeryCounter]: REGISTRY_BLOCK.presentation.liveBakeryCounterList,
  [REGISTRY_BLOCK.type.socialFeed]: REGISTRY_BLOCK.presentation.socialFeedGrid,
  [REGISTRY_BLOCK.type.coffeeSubscription]: REGISTRY_BLOCK.presentation.coffeeSubscriptionMatrix,
  [REGISTRY_BLOCK.type.preOrderPickup]: REGISTRY_BLOCK.presentation.preOrderPickupForm,
  [REGISTRY_BLOCK.type.drinksList]: REGISTRY_BLOCK.presentation.drinksListStatic,
  [REGISTRY_BLOCK.type.cocktailSpotlight]: REGISTRY_BLOCK.presentation.dynamicCocktailSpotlight,
  [REGISTRY_BLOCK.type.privatePartyInquiry]: REGISTRY_BLOCK.presentation.privatePartyInquiryForm,
  [REGISTRY_BLOCK.type.liveTapList]: REGISTRY_BLOCK.presentation.liveTapListSync,
  [REGISTRY_BLOCK.type.vipTableBooking]: REGISTRY_BLOCK.presentation.vipTableBookingStrip,
  [REGISTRY_BLOCK.type.staticLocation]: REGISTRY_BLOCK.presentation.staticLocationText,
  [REGISTRY_BLOCK.type.simpleMenuStrip]: REGISTRY_BLOCK.presentation.simpleMenuStrip,
  [REGISTRY_BLOCK.type.liveGps]: REGISTRY_BLOCK.presentation.liveGpsLocationEmbed,
  [REGISTRY_BLOCK.type.weeklySchedule]: REGISTRY_BLOCK.presentation.weeklyScheduleRouteStrip,
  [REGISTRY_BLOCK.type.popUpTicket]: REGISTRY_BLOCK.presentation.popUpTicketPurchaseCard,
  [REGISTRY_BLOCK.type.cateringInquiry]: REGISTRY_BLOCK.presentation.cateringInquiryForm,
  [REGISTRY_BLOCK.type.legalImprint]: REGISTRY_BLOCK.presentation.legalImprintBlock,
  [REGISTRY_BLOCK.type.legalPrivacy]: REGISTRY_BLOCK.presentation.legalPrivacyBlock,
  [REGISTRY_BLOCK.type.contact]: REGISTRY_BLOCK.presentation.contactVisit,
  [REGISTRY_BLOCK.type.faq]: REGISTRY_BLOCK.presentation.faqAccordion,
  [REGISTRY_BLOCK.type.backToTop]: REGISTRY_BLOCK.presentation.backToTopCorner,
  [REGISTRY_BLOCK.type.contactChannels]: REGISTRY_BLOCK.presentation.contactChannelsRail,
  [REGISTRY_BLOCK.type.utilityBar]: REGISTRY_BLOCK.presentation.utilityBarSticky,
  [REGISTRY_BLOCK.type.announcementBar]: REGISTRY_BLOCK.presentation.announcementBarBand,
};

/**
 * Legacy chrome-encoded Navigation_* keys (Arrangement = Rail).
 * Prefer `Navigation_Rail` + `content.navPresentation`.
 */
export const NAV_PRESENTATION_FROM_REGISTRY: Partial<
  Record<RegistryBlockPresentation, string>
> = {
  [REGISTRY_BLOCK.presentation.navigationClassic]: 'classic',
  [REGISTRY_BLOCK.presentation.navigationHeader]: 'classic',
  [REGISTRY_BLOCK.presentation.navigationOverlay]: 'overlay',
  [REGISTRY_BLOCK.presentation.navigationQuiet]: 'quiet',
  [REGISTRY_BLOCK.presentation.navigationFrosted]: 'frosted',
  [REGISTRY_BLOCK.presentation.navigationConversion]: 'conversion',
  [REGISTRY_BLOCK.presentation.navigationWhisper]: 'whisper',
  [REGISTRY_BLOCK.presentation.navigationEditorial]: 'editorial',
};

/** Nav Arrangement axis — structure (desktop bar + phone open ritual), independent of chrome. */
export const NAV_ARRANGEMENT = {
  /** Market-standard F&B bar: brand left · links · locale · CTA; phone right sheet. */
  rail: 'rail',
  /** Centered brand on desktop; centered drawer header on phone. */
  compass: 'compass',
  /** Rail bar topology + full-bleed phone panel (ceremony / editorial). */
  curtain: 'curtain',
  /** Rail bar topology + bottom-sheet phone panel (thumb / conversion). */
  ledger: 'ledger',
  /**
   * Rail bar topology + single-mark toggle + narrow edge panel on phone.
   * (Revived with phone-first ritual — not the old brand-letter-only bar.)
   */
  monogram: 'monogram',
  /**
   * Rail bar topology + phone sheet that features Local Presence social links.
   * Social icons are Arrangement-owned — not shown on Rail / Compass / etc.
   */
  signal: 'signal',
  /** @deprecated Locale is universal; Harbor was brand-left + centered links only → rail. */
  harbor: 'harbor',
  /** @deprecated Mega-menu bars are rare for small restaurant sites → rail. */
  gallery: 'gallery',
} as const;

/**
 * Selectable arrangements — each is a full responsive cut (desktop + phone open ritual).
 * Design variety also comes from nav *chrome* (overlay / quiet / …) paired with heroes.
 */
export const NAV_ARRANGEMENT_VALUES = [
  NAV_ARRANGEMENT.rail,
  NAV_ARRANGEMENT.compass,
  NAV_ARRANGEMENT.curtain,
  NAV_ARRANGEMENT.ledger,
  NAV_ARRANGEMENT.monogram,
  NAV_ARRANGEMENT.signal,
] as const;

export type NavArrangement = (typeof NAV_ARRANGEMENT_VALUES)[number];

export type NavArrangementLegacy = 'harbor' | 'gallery';

export function isNavArrangement(value: string): value is NavArrangement {
  return (NAV_ARRANGEMENT_VALUES as readonly string[]).includes(value);
}

/** Map legacy / stored arrangement ids onto the live catalog. */
export function normalizeNavArrangement(value?: string | null): NavArrangement {
  if (value === NAV_ARRANGEMENT.harbor || value === NAV_ARRANGEMENT.gallery) {
    return NAV_ARRANGEMENT.rail;
  }
  if (value && isNavArrangement(value)) {
    return value;
  }
  return NAV_ARRANGEMENT.rail;
}

/** Canonical arrangement → registry key (chrome always on content.navPresentation). */
export const NAV_ARRANGEMENT_PRESENTATION: Readonly<
  Record<NavArrangement | NavArrangementLegacy, RegistryBlockPresentation>
> = {
  [NAV_ARRANGEMENT.rail]: REGISTRY_BLOCK.presentation.navigationRail,
  [NAV_ARRANGEMENT.compass]: REGISTRY_BLOCK.presentation.navigationCompass,
  [NAV_ARRANGEMENT.curtain]: REGISTRY_BLOCK.presentation.navigationCurtain,
  [NAV_ARRANGEMENT.ledger]: REGISTRY_BLOCK.presentation.navigationLedger,
  [NAV_ARRANGEMENT.monogram]: REGISTRY_BLOCK.presentation.navigationMonogram,
  [NAV_ARRANGEMENT.signal]: REGISTRY_BLOCK.presentation.navigationSignal,
  [NAV_ARRANGEMENT.harbor]: REGISTRY_BLOCK.presentation.navigationHarbor,
  [NAV_ARRANGEMENT.gallery]: REGISTRY_BLOCK.presentation.navigationGallery,
};

/** @deprecated Chrome-encoded Rail keys — aliases for Navigation_Rail. */
const NAV_RAIL_CHROME_PRESENTATIONS = [
  REGISTRY_BLOCK.presentation.navigationClassic,
  REGISTRY_BLOCK.presentation.navigationOverlay,
  REGISTRY_BLOCK.presentation.navigationQuiet,
  REGISTRY_BLOCK.presentation.navigationFrosted,
  REGISTRY_BLOCK.presentation.navigationConversion,
  REGISTRY_BLOCK.presentation.navigationWhisper,
  REGISTRY_BLOCK.presentation.navigationEditorial,
] as const;

const NAV_RAIL_CHROME_SET = new Set<string>(NAV_RAIL_CHROME_PRESENTATIONS);

export function isNavRailChromePresentation(value: string): boolean {
  return NAV_RAIL_CHROME_SET.has(value);
}

/**
 * Normalize stored nav registry keys.
 * Deprecated Harbor / Gallery → Rail. Compass / Curtain / Ledger / Monogram stay.
 * Chrome-as-key aliases → Rail.
 */
export function normalizeNavRegistryKey(
  registryKey?: string | null,
): RegistryBlockPresentation {
  if (!registryKey) {
    return REGISTRY_BLOCK.presentation.navigationRail;
  }
  if (registryKey === REGISTRY_BLOCK.presentation.navigationCompass) {
    return REGISTRY_BLOCK.presentation.navigationCompass;
  }
  if (registryKey === REGISTRY_BLOCK.presentation.navigationCurtain) {
    return REGISTRY_BLOCK.presentation.navigationCurtain;
  }
  if (registryKey === REGISTRY_BLOCK.presentation.navigationLedger) {
    return REGISTRY_BLOCK.presentation.navigationLedger;
  }
  if (registryKey === REGISTRY_BLOCK.presentation.navigationMonogram) {
    return REGISTRY_BLOCK.presentation.navigationMonogram;
  }
  if (registryKey === REGISTRY_BLOCK.presentation.navigationSignal) {
    return REGISTRY_BLOCK.presentation.navigationSignal;
  }
  if (
    registryKey === REGISTRY_BLOCK.presentation.navigationHarbor ||
    registryKey === REGISTRY_BLOCK.presentation.navigationGallery
  ) {
    return REGISTRY_BLOCK.presentation.navigationRail;
  }
  if (
    registryKey === REGISTRY_BLOCK.presentation.navigationHeader ||
    isNavRailChromePresentation(registryKey) ||
    registryKey === REGISTRY_BLOCK.presentation.navigationRail
  ) {
    return REGISTRY_BLOCK.presentation.navigationRail;
  }
  if (
    isRegistryBlockPresentation(registryKey) &&
    PRESENTATION_TO_BLOCK_TYPE[registryKey] === REGISTRY_BLOCK.type.navigation
  ) {
    return REGISTRY_BLOCK.presentation.navigationRail;
  }
  return REGISTRY_BLOCK.presentation.navigationRail;
}

export function resolveNavArrangementFromRegistry(
  registryKey?: string | null,
): NavArrangement {
  const normalized = normalizeNavRegistryKey(registryKey);
  if (normalized === REGISTRY_BLOCK.presentation.navigationCompass) {
    return NAV_ARRANGEMENT.compass;
  }
  if (normalized === REGISTRY_BLOCK.presentation.navigationCurtain) {
    return NAV_ARRANGEMENT.curtain;
  }
  if (normalized === REGISTRY_BLOCK.presentation.navigationLedger) {
    return NAV_ARRANGEMENT.ledger;
  }
  if (normalized === REGISTRY_BLOCK.presentation.navigationMonogram) {
    return NAV_ARRANGEMENT.monogram;
  }
  if (normalized === REGISTRY_BLOCK.presentation.navigationSignal) {
    return NAV_ARRANGEMENT.signal;
  }
  return NAV_ARRANGEMENT.rail;
}

/** Registry key for an arrangement. Chrome is never encoded in the key. */
export function registryKeyForNavArrangement(
  arrangement: NavArrangement,
  _chrome?: string,
): RegistryBlockPresentation {
  return NAV_ARRANGEMENT_PRESENTATION[normalizeNavArrangement(arrangement)];
}

/**
 * Presentations for a block type (Arrangement catalog entries).
 * Nav: arrangement keys only (drops chrome-as-key aliases + Navigation_Header).
 */
export function listPresentationsForBlockType(
  blockType: RegistryBlockType,
  options?: { includeDeprecated?: boolean },
): RegistryBlockPresentation[] {
  return REGISTRY_BLOCK_PRESENTATION_VALUES.filter((presentation) => {
    if (PRESENTATION_TO_BLOCK_TYPE[presentation] !== blockType) {
      return false;
    }
    if (!options?.includeDeprecated) {
      if (presentation === REGISTRY_BLOCK.presentation.navigationHeader) {
        return false;
      }
      if (
        presentation === REGISTRY_BLOCK.presentation.navigationHarbor ||
        presentation === REGISTRY_BLOCK.presentation.navigationGallery
      ) {
        return false;
      }
      if (
        blockType === REGISTRY_BLOCK.type.navigation &&
        isNavRailChromePresentation(presentation)
      ) {
        return false;
      }
    }
    return true;
  });
}

/** Hero Arrangement options (all hero presentations). */
export function listHeroArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.hero);
}

/** Static_Menu Arrangement options for the full Speisekarte (excludes Home highlight looks). */
export function listStaticMenuArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.staticMenu).filter(
    (key) => !String(key).startsWith('Static_Menu_Highlight_'),
  );
}

/** Footer Arrangement options (Standard · Colophon · Directory · Utility). */
export function listFooterArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.footer);
}

/** Contact Arrangement options (Visit · Desk · Ledger · Threshold). */
export function listContactArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.contact);
}

/** Back-to-top Arrangement options (Corner disc · Transparent icon). */
export function listBackToTopArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.backToTop);
}

/** Contact channels Arrangement options (Rail · Fab). */
export function listContactChannelsArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.contactChannels);
}

/** Utility Bar Arrangement options (Static · Sticky · Carousel · Collapse). */
export function listUtilityBarArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.utilityBar);
}

/** Image Gallery Arrangement options (Mosaic · Masonry · Filmstrip · Spotlight · Ledger). */
export function listImageGalleryArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.imageGallery);
}

/** Degustationsmenü arrangements (course timeline · pairing cards). */
export function listTastingMenuArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.tastingTimeline);
}

/** Drinks list arrangements (static columns · cellar ledger). */
export function listDrinksListArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.drinksList);
}

/**
 * Highlight / curated list arrangements — item-first, no category filter UX.
/** Kitchen story arrangements (quote · portrait split · compact band · notes). */
export function listChefStoryArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.chefStory);
}

/** Kitchen team arrangements (lead · row · band). */
export function listKitchenTeamArrangementPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.kitchenTeam);
}

/**
 * Distinct looks from the full Speisekarte (favorites, specials, lunch, happy hour…).
 * PDF / category gallery / editorial / classic multi-column stay on the Menu page.
 */
export function listHighlightMenuArrangementPresentations(): RegistryBlockPresentation[] {
  return [
    REGISTRY_BLOCK.presentation.staticMenuHighlightStrip,
    REGISTRY_BLOCK.presentation.staticMenuHighlightHeroPlate,
    REGISTRY_BLOCK.presentation.staticMenuHighlightMarquee,
    REGISTRY_BLOCK.presentation.staticMenuHighlightZine,
    REGISTRY_BLOCK.presentation.staticMenuHighlightChalkboard,
    REGISTRY_BLOCK.presentation.staticMenuHighlightChefsCut,
    REGISTRY_BLOCK.presentation.staticMenuHighlightTodaySplit,
    REGISTRY_BLOCK.presentation.staticMenuHighlightPriceTiles,
    REGISTRY_BLOCK.presentation.staticMenuHighlightDishCards,
  ];
}

/** True when a menu block is a curated highlight projection (not the full Menu Book page). */
export function isHighlightMenuCatalogBinding(
  binding: { kind?: string; itemIds?: unknown } | null | undefined,
): boolean {
  return binding?.kind === 'menu_catalog' && Array.isArray(binding.itemIds);
}

/** Registry key belongs to the Home highlight arrangement family. */
export function isHighlightMenuPresentation(presentation: string | null | undefined): boolean {
  return Boolean(presentation && presentation.startsWith('Static_Menu_Highlight_'));
}

/** Reservation channel options (inquiry · third-party · direct WhatsApp/phone). */
export function listReservationChannelPresentations(): RegistryBlockPresentation[] {
  return listPresentationsForBlockType(REGISTRY_BLOCK.type.reservation).filter(
    (key) => key !== REGISTRY_BLOCK.presentation.reservationCallBook,
  );
}

/** Include Call only when the block already uses it (legacy alias of direct contact). */
export function listReservationChannelPresentationsForBlock(
  currentPresentation: string | null | undefined,
): RegistryBlockPresentation[] {
  const base = listReservationChannelPresentations();
  if (currentPresentation === REGISTRY_BLOCK.presentation.reservationCallBook) {
    return [...base, REGISTRY_BLOCK.presentation.reservationCallBook];
  }
  return base;
}

export function isPlatformVerticalKey(value: string): value is PlatformVerticalKey {
  return (PLATFORM_VERTICAL_KEY_VALUES as readonly string[]).includes(value);
}

export function isPackageTierKey(value: string): value is PackageTierKey {
  return (PACKAGE_TIER_KEY_VALUES as readonly string[]).includes(value);
}

export function isThemeId(value: string): value is ThemeId {
  return (THEME_ID_KEYS as readonly string[]).includes(value);
}

export function isColorSetId(value: string): value is ColorSetId {
  return (COLORSET_ID_KEYS as readonly string[]).includes(value);
}

export function isRegistryBlockType(value: string): value is RegistryBlockType {
  return (REGISTRY_BLOCK_TYPE_VALUES as readonly string[]).includes(value);
}

export function isRegistryBlockPresentation(value: string): value is RegistryBlockPresentation {
  return (REGISTRY_BLOCK_PRESENTATION_VALUES as readonly string[]).includes(value);
}

export function resolveBlockTypeForPresentation(
  presentation: string,
): RegistryBlockType | undefined {
  if (!isRegistryBlockPresentation(presentation)) {
    return undefined;
  }
  return PRESENTATION_TO_BLOCK_TYPE[presentation];
}
